import React from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';

// Translation
import translate from '../../translate/Translate';

// Components
import BarChart from '../../charts/BarChart';
import Metadata from '../../common/report/Metadata';
import Tooltip from '../../common/Tooltip';
import Date from '../../common/Date';
import SurveyModal from './SurveyModal';



const ReportPage = observer(
  class ReportPage extends React.Component {
    constructor() {
      super();

      this.lastPage = null;

      this.state = {
        isCompleted: false,
        showSurvey: false
      }

    }
    
    componentDidMount() {
      const handlePrint = (attempts = 0) => {
        if (attempts >= 10) {
          // Some reports might not have a button. If we can't find the button after 10 attempts stop loop
          return;
        };

        const btn = document.querySelector('#save-report');

        if (btn) {
          btn.onclick = () => {
            window.print();
          }
        } else {
          setTimeout(() => handlePrint(++attempts), 100);
        }
      }

      handlePrint();
    }

    componentDidUpdate() {
      if (this.state.showSurvey) {
        // This is to disable scrolling while the modal is active.
        document.body.style.overflow = 'hidden';
      } else if (!this.state.showSurvey) {
        // Allows scrolling again
        document.body.style.overflow = '';
      }
     }


    updateActivityHistory = () => {
      const { page, store } = this.props;
      const { ActivityStore, AppStore } = store;
      const { locale } = AppStore;

      ActivityStore.sendPageViewActivity({
        pageUrl: window.location.href,
        view: this.constructor.name,
        title: page.title[locale],
        reportSid: page.reportSid
      });
    };

    // Used to set state from child component
    surveyState = () => {
      this.setState({
        showSurvey: !this.state.showSurvey
      })
    }

    render() {
      const {
        attemptCount,
        handleResource,
        lastCompletedDate,
        page,
        store,
        translation
      } = this.props;
      const { AppStore, UserStore, SurveyStore } = store;
      const { locale } = AppStore;
      const { role } = UserStore.userData;


      if (this.lastPage !== page.title[locale]) {
        this.updateActivityHistory();
        // Check if a survey needs to be shown
        SurveyStore.checkForSurvey(this.surveyState);
      }
      this.lastPage = page.title[locale];

      return (
        <div className="page__content wrap">
          <div className="page__primary">
            <div className="page__header">
              <h1 id="page-title">
                {page.title[locale]}
              </h1>

              {attemptCount &&
                <span className="page__subtitle">
                  {attemptCount === 1 ? translation.attempt : translation.attempts.replace('%count%', attemptCount)}
                </span>
              }

              {lastCompletedDate &&
                <span className="page__subtitle">
                  {translation.last_completed}

                  <Date
                    {...this.props}
                    date={lastCompletedDate}
                    dateFormat="M/d/yy h:mm a"
                  />
                </span>
              }

              {/* eslint-disable */}
              {role.toLowerCase() !== 'learner' && attemptCount &&
                <a
                  className="page__subtitle"
                  href={`/assessment-review/${page.assessmentSid}?attempt=${page.attemptSid}&assessmentVersionSid=${page.assessmentVersionSid}`}
                  target="_blank"
                  rel="opener noreferrer"
                >
                  {translation.view_responses}
                </a>
              }
              {/* eslint-enable */}
            </div>

            {page.sections.map((section, i) => (
              <section key={i} className="page__section" aria-labelledby={`section-head-${i}`}>
                {section.heading && section.heading[locale] && (
                  <h2 className="page__heading" id={`section-head-${i}`}>{section.heading[locale]}</h2>
                )}

                {section.content.map((content, j) => {
                  if (content.type === 'text') {
                    return (
                      <div
                        key={j}
                        dangerouslySetInnerHTML={{ __html: content.value[locale] }}
                      />
                    );
                  }

                  if (content.type === 'metadata') {
                    return (
                      <Metadata
                        meta={content.value}
                        translation_name={translation.general_information.name}
                        translation_started={translation.general_information.started}
                        translation_completed={translation.general_information.completed}
                      />
                    );
                  }

                  if (content.type === 'resource-links') {
                    // Return a single link there's only one
                    if (content.value.links.length === 1) {
                      const singleLink = content.value.links[0];

                      return (
                        <div key={j} className="resource-list">
                          <div className="resource-list__item">
                            <a
                              href={singleLink.url}
                              className="resource-list__link"
                              onClick={() => handleResource(singleLink, page.reportSid)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {singleLink.label[locale]}
                            </a>
                          </div>
                        </div>
                      )

                    } else {
                      // Return a structured list if there's more than one
                      return (
                        <ul key={j} className="resource-list">
                          {content.value.links.map((link, k) => (
                            <li key={k} className="resource-list__item">
                              <a
                                href={link.url}
                                className="resource-list__link"
                                onClick={() => handleResource(link, page.reportSid)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {link.label[locale]}
                              </a>
                            </li>
                          ))}
                        </ul>
                      );
                    }
                  }

                  /**
                  The field for image alt doesn't exist yet in the API,
                  adding this placeholder here for now.
                */
                  if (content.type === 'image') {
                    return (
                      <figure key={j} className="report__image">
                        <img
                          //TODO: make this work so it passes the react checking during build. The error indicated no alt tag was present..
                          //{...(content.value.alt && { alt: content.value.alt })}
                          alt="this is a visual for the report"
                          src={content.value.sourceUrl}
                          width={content.value.size.width}
                          height={content.value.size.height}
                        />
                      </figure>
                    );
                  }

                  if (
                    content.type === 'coaching-tip' &&
                    (role.toLowerCase() === 'administrator' ||
                      role.toLowerCase() === 'instructor')
                  ) {
                    return (
                      <aside key={j} className="note">
                        <h3 className="note__title">{translation.note.heading}</h3>
                        <div
                          className="note__body"
                          dangerouslySetInnerHTML={{ __html: content.value[locale] }}
                        />
                      </aside>
                    );
                  }

                  // Bar graph
                  if (content.type === 'graph' && content.value.type === 'bar') {
                    // Reformat data for BarChart component
                    const data = [];

                    content.value.bars.forEach(bar => {
                      data.push({
                        label: bar.title[locale],
                        value: [bar['percentage-filled']],
                        className: bar.colorClass
                      });
                    });

                    // Return the BarChart
                    return (
                      <figure key={j} className="report__barchart">
                        <BarChart
                          data={data}
                          emptyText={translation.chart.empty_text}
                          hideScores
                          units="%"
                          max={100}
                          yLabels={translation.chart.y_labels}
                          levels={3}
                        />
                      </figure>
                    );
                  }

                  // View responses button
                  if (content.type === 'response-button') {
                    return (
                      <Link
                        to={`/assessment-review/${content.value.assessmentSid}?attempt=${content.value.attemptSid}`}
                        className="btn btn--outline btn--full"
                        target="_blank"
                      >
                        <Tooltip
                          content={translation.new_tab}
                          id={`view-responses-${j}`}
                          icon={translation.view_responses}
                          brief
                          hideLabel
                          up
                        />
                      </Link>
                    );
                  }

                  return null;
                })}
              </section>
            ))}
          </div>
          {this.state.showSurvey &&
            <SurveyModal
              {...this.props}
              surveyJson={SurveyStore.surveyJson}
              surveyState={this.surveyState}
            />
          }
        </div>
      );
    }
  }
);

export default translate('ReportPage')(ReportPage);
